import React, { useMemo, useState } from 'react';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { Banner, ErrorHandler, PageSteps } from '@common/components';
import { createMessage } from 'works/legacy/lib/message-helper';
import { MembersUpdateLocationsConfirmModal } from './members-update-locations-confirm-modal';
import {
  MembershipWithDisable,
  MembersUpdateLocationsStepSelectMembers,
} from './members-update-locations-step-select-members';
import { MembersUpdateLocationsStepSelectLocations } from './members-update-locations-step-select-locations';
import { MembersUpdateLocationsStepReview } from './members-update-locations-step-review';
import { useMutation, useQuery, useNavigate, useLocation } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import {
  Membership,
  WorksMembershipsQuery,
  WorksMembershipsResponse,
} from 'works/legacy/graphql/works-memberships';
import { ORGANIZATION_UNITS_QUERY } from '@institutions/graphql';
import {
  BatchAddWorksMembershipsOrganizationUnitsQuery,
  BatchAddWorksMembershipsOrganizationUnitsResponse,
  BatchAddWorksMembershipsOrganizationUnitsVariables,
} from 'works/legacy/graphql/batch-add-works-memberships-organization-units';

// There are no existing constant for Int32 max value in Javascript
const INT32_MAX_VALUE = 2147483647;

export function MembersUpdateLocationsSteps() {
  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: ORGANIZATION_ADMIN_MENU.USERS.LABEL,
      path: ORGANIZATION_ADMIN_MENU.USERS.ROUTE,
    },
    {
      name: 'Update Locations',
      path: `${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/update-locations`,
    },
  ]);

  const navigate = useNavigate();
  const currentMembership = useCurrentMembership();
  const { state } = useLocation() as {
    state: { memberIds?: string[] };
  };

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(
    state?.memberIds || []
  );
  const [selectedOrganizationUnitsIds, setSelectedOrganizationUnitsIds] =
    useState<string[]>([]);

  const worksMembershipsResponse = useQuery<WorksMembershipsResponse>(
    WorksMembershipsQuery,
    {
      variables: {
        // Transfer does not support backend pagination yet.
        input: { pagination: { pageSize: INT32_MAX_VALUE } },
      },
    }
  );

  const organizationUnitsResponse = useQuery(ORGANIZATION_UNITS_QUERY);

  const organizationUnits = useMemo(() => {
    return (
      /*
      There is a logic in the policy that brings the units which have relationship with the user existing assignment terms.
      As we have the visible property, we can filter them as a workaround. Issue WORK-4931.
      */
      organizationUnitsResponse.data?.organizationUnits?.filter(
        (unit) => unit.visible
      ) || []
    );
  }, [organizationUnitsResponse]);

  const canCurrentMembershipUpdateAdminUnits =
    currentMembership.canUpdateAdminUsersUnits;

  const worksMemberships = useMemo(() => {
    const worksMemberships: MembershipWithDisable[] =
      worksMembershipsResponse.data?.worksMemberships?.collection || [];

    return worksMemberships.map(disableUnpermittedMemberships);
  }, [worksMembershipsResponse]);

  const selectedOrganizationUnits = useMemo(() => {
    return organizationUnits.filter(({ id }) =>
      selectedOrganizationUnitsIds.includes(id)
    );
  }, [organizationUnits, selectedOrganizationUnitsIds]);

  const selectedMembers = useMemo(() => {
    return worksMemberships.filter(({ id }) => selectedMemberIds.includes(id));
  }, [worksMemberships, selectedMemberIds]);

  const isPlural = selectedMembers.length > 1;

  const [save] = useMutation<
    BatchAddWorksMembershipsOrganizationUnitsResponse,
    BatchAddWorksMembershipsOrganizationUnitsVariables
  >(BatchAddWorksMembershipsOrganizationUnitsQuery, {
    onCompleted(data) {
      const plural = isPlural ? 's' : '';
      const totalMembershipsUpdated =
        data.batchAddWorksMembershipsOrganizationUnits.worksMemberships.length;

      createMessage(
        'success',
        `A total of ${totalMembershipsUpdated} user${plural} had their locations updated`
      );

      navigate(ORGANIZATION_ADMIN_MENU.USERS.ROUTE, {
        state: {
          updatedMemberIds: selectedMemberIds,
        },
      });
    },
    onError: () =>
      createMessage('error', 'Something went wrong. Try again later.'),
  });

  const anyQueryError =
    worksMembershipsResponse.error || organizationUnitsResponse.error;

  if (anyQueryError) {
    return (
      <ErrorHandler error={anyQueryError} log="MembersUpdateLocationsSteps" />
    );
  }

  function handleSave() {
    save({
      variables: {
        input: {
          worksMembershipIds: selectedMemberIds,
          organizationUnitIds: selectedOrganizationUnitsIds,
        },
      },
    });
  }

  const hasSelectedMembers = !!selectedMemberIds?.length;

  return (
    <>
      <MembersUpdateLocationsConfirmModal
        visible={showConfirmModal}
        onConfirm={handleSave}
        onCancel={() => setShowConfirmModal(false)}
      />
      <PageSteps
        initialStepIndex={state?.memberIds?.length ? 1 : 0}
        onCancel={() => {
          navigate(ORGANIZATION_ADMIN_MENU.USERS.ROUTE);
        }}
        steps={[
          {
            title: 'User Selection',
            page: (
              <MembersUpdateLocationsStepSelectMembers
                worksMemberships={worksMemberships}
                loading={worksMembershipsResponse.loading}
                onWorksMembershipIdsChange={setSelectedMemberIds}
                selectedWorksMembershipIds={selectedMemberIds}
              />
            ),
            forwardButtonDisabled: !hasSelectedMembers,
            headerSlot: !canCurrentMembershipUpdateAdminUnits &&
              !worksMembershipsResponse.loading && (
                <Banner type="info">
                  You don&apos;t have the rights to select Organizations Admin
                  users.
                </Banner>
              ),
          },
          {
            title: 'Units',
            page: (
              <MembersUpdateLocationsStepSelectLocations
                organizationUnits={organizationUnits}
                loading={organizationUnitsResponse.loading}
                selectedOrganizationUnitIds={selectedOrganizationUnitsIds}
                onOrganizationUnitIdsChange={setSelectedOrganizationUnitsIds}
              />
            ),
            forwardButtonDisabled: !selectedOrganizationUnitsIds.length,
          },
          {
            title: 'Summary',
            page: (
              <MembersUpdateLocationsStepReview
                organizationUnits={selectedOrganizationUnits}
                members={selectedMembers}
              />
            ),
          },
        ]}
        onConfirm={() => {
          if (!hasSelectedMembers) {
            createMessage('error', 'No members selected.');
            return;
          }
          setShowConfirmModal(true);
        }}
      />
    </>
  );
}

function disableUnpermittedMemberships(membership: Membership) {
  return {
    ...membership,
    disabled: !!membership && !membership.canUserUpdateMembership,
  };
}
